/**
 * Fetch wrapper. A 401 sends the visitor to the sign-in sheet (spec 247).
 */
export async function apiFetch(
  input: string,
  init?: RequestInit,
): Promise<Response> {
  const headers: Record<string, string> = {
    ...init?.headers as Record<string, string>,
  };

  // Default Content-Type to JSON when there's a body and the caller didn't
  // already set one (e.g. CSV uploads pass text/csv).
  const hasContentType = Object.keys(headers).some(
    (k) => k.toLowerCase() === 'content-type',
  );
  if (init?.body && !hasContentType) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(input, {
    ...init,
    headers,
  });

  if (response.status === 401) {
    // Spec 247 — to Kridt's sign-in sheet, not straight to the identity
    // provider's page (which the design review found unbranded).
    //
    // NEVER from the sheet itself. It renders outside the auth providers and
    // makes no protected request, but if anything ever did, redirecting from
    // /signin to /signin would reload it into the same 401 — an infinite loop
    // that only production can show, since ORBIT_DEV never returns a 401.
    // This guard makes the loop impossible rather than merely unlikely.
    if (!window.location.pathname.startsWith('/signin')) {
      window.location.href = '/signin';
    }
    // Return a never-resolving promise to prevent further processing
    return new Promise(() => {});
  }

  return response;
}

/**
 * Typed JSON fetch helper.
 */
export async function apiGet<T>(url: string): Promise<T> {
  const res = await apiFetch(url);
  if (!res.ok) {
    // Keep the message string stable (callers read `.message`); additively
    // attach the HTTP status so callers can branch on it (e.g. friendly 404).
    const err = new Error(`API error: ${res.status} ${res.statusText}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}
